import React, { useCallback, useEffect, useState } from 'react';
import ConfirmModal from '../../../../components/common/ConfirmModal/ConfirmModal';
import FormInput from '../../../../components/common/FormInput/FormInput';
import SearchableSelect from '../../../../components/common/SearchableSelect/SearchableSelect';
import { StudentService } from '../../../../services';
import {
  PERFORMANCE_SUPPORT_REASON_LABELS,
  PERFORMANCE_SUPPORT_REASON_OPTIONS,
  isPerformanceSupportReason,
  type PerformanceSupportReason,
  type PerformanceSupportState,
} from '../../../../types/performanceSupport';
import { formatDate } from '../../../../utils';
import { getBrazilCivilDate } from '../../../../utils/competence';
import layoutStyles from '../StudentRegister.module.css';
import styles from './PerformanceSupportSection.module.css';

interface PerformanceSupportSectionProps {
  studentId: number;
  readOnly?: boolean;
  /** When true, omits the page title (e.g. inside Histórico accordion). */
  embedded?: boolean;
  onChanged?: () => void;
}

const emptyState = (): PerformanceSupportState => ({
  latest: null,
  canRegister: false,
  nextRegisterOn: null,
  history: [],
});

function registeredOnLabel(createdAt: string): string | null {
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return formatDate(getBrazilCivilDate(parsed));
}

function validatePregnantFrom(value: string): string | null {
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return 'Informe a data em que a aluna ficou grávida.';
  }

  if (trimmed > getBrazilCivilDate()) {
    return 'A data em que ficou grávida não pode ser no futuro.';
  }

  return null;
}

const PerformanceSupportSection: React.FC<PerformanceSupportSectionProps> = ({
  studentId,
  readOnly = false,
  embedded = false,
  onChanged,
}) => {
  const [state, setState] = useState<PerformanceSupportState>(emptyState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [displayReason, setDisplayReason] = useState('');
  const [pendingReason, setPendingReason] = useState<PerformanceSupportReason | null>(null);
  const [pregnantFrom, setPregnantFrom] = useState('');
  const [pregnantFromError, setPregnantFromError] = useState<string | null>(null);

  const loadSupport = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const nextState = await StudentService.getPerformanceSupport(studentId);
      setState({
        ...nextState,
        history: Array.isArray(nextState.history) ? nextState.history : [],
      });
      setDisplayReason(nextState.latest?.reason ?? '');
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Não foi possível carregar o suporte da performance.');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [studentId]);

  useEffect(() => {
    setPendingReason(null);
    setDisplayReason('');
    setPregnantFrom('');
    setPregnantFromError(null);
    void loadSupport();
  }, [loadSupport]);

  const savedReason = state.latest?.reason ?? '';

  const cancelChange = () => {
    if (saving) {
      return;
    }

    setDisplayReason(savedReason);
    setPendingReason(null);
    setPregnantFrom('');
    setPregnantFromError(null);
    setError(null);
  };

  const handleReasonChange = (value: string) => {
    if (saving || readOnly || !isPerformanceSupportReason(value) || value === savedReason) {
      return;
    }

    setDisplayReason(value);
    setPendingReason(value);
    setPregnantFrom('');
    setPregnantFromError(null);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!pendingReason) {
      return;
    }

    const needsPregnantFrom = pendingReason === 'became_pregnant';
    if (needsPregnantFrom) {
      const dateError = validatePregnantFrom(pregnantFrom);
      if (dateError) {
        setPregnantFromError(dateError);
        return;
      }
    }

    const createsNewRecord = !state.latest || state.canRegister;
    setSaving(true);
    setError(null);
    try {
      const pregnantFromValue = needsPregnantFrom ? pregnantFrom.trim() : null;
      if (createsNewRecord) {
        await StudentService.savePerformanceSupport(studentId, pendingReason, pregnantFromValue);
      } else {
        await StudentService.updatePerformanceSupport(studentId, pendingReason, pregnantFromValue);
      }
      setPendingReason(null);
      setPregnantFrom('');
      setPregnantFromError(null);
      await loadSupport(true);
      onChanged?.();
    } catch (saveError) {
      setError(saveError instanceof Error
        ? saveError.message
        : 'Não foi possível trocar o suporte da performance.');
    } finally {
      setSaving(false);
    }
  };

  const pendingLabel = pendingReason ? PERFORMANCE_SUPPORT_REASON_LABELS[pendingReason] : '';
  const registeredOn = state.latest ? registeredOnLabel(state.latest.createdAt) : null;
  const sectionClassName = [
    styles.section,
    embedded ? styles.embedded : '',
  ].filter(Boolean).join(' ');

  return (
    <section className={sectionClassName}>
      <div className={styles.header}>
        {embedded ? (
          <span className={styles.embeddedLabel}>Registrar</span>
        ) : (
          <h2 className={layoutStyles.columnTitle}>Suporte da performance</h2>
        )}
      </div>

      {loading ? (
        <p className={styles.status}>Carregando suporte da performance...</p>
      ) : (
        <>
          <div className={styles.selectWrap}>
            <SearchableSelect
              value={displayReason}
              options={PERFORMANCE_SUPPORT_REASON_OPTIONS}
              onChange={handleReasonChange}
              placeholder="Selecione o suporte"
              searchPlaceholder="Buscar suporte..."
              allowEmpty={false}
              disabled={saving}
              readOnly={readOnly}
            />
          </div>
          {!state.latest && !error && (
            <p className={styles.summary}>Suporte ainda não informado.</p>
          )}
          {registeredOn && (
            <p className={styles.summary}>Registrado em {registeredOn}.</p>
          )}
          {error && <p className={styles.error}>{error}</p>}
        </>
      )}

      <ConfirmModal
        open={pendingReason !== null}
        title="Suporte da performance"
        variant={pendingReason === 'became_pregnant' ? 'warning' : 'info'}
        alignment="left"
        width={520}
        loading={saving}
        confirmLabel="Confirmar"
        cancelLabel="Cancelar"
        onCancel={cancelChange}
        onConfirm={() => {
          void handleConfirm();
        }}
      >
        {pendingLabel ? (
          <div className={styles.confirmBody}>
            <p className={styles.confirmMessage}>
              Deseja trocar o suporte da performance para “{pendingLabel}”?
            </p>
            {pendingReason === 'became_pregnant' ? (
              <>
                <FormInput
                  label="Data em que ficou grávida"
                  type="date"
                  value={pregnantFrom}
                  max={getBrazilCivilDate()}
                  required
                  error={pregnantFromError ?? undefined}
                  onChange={(event) => {
                    setPregnantFrom(event.target.value);
                    setPregnantFromError(null);
                  }}
                />
                <p className={styles.pregnancyNotice}>
                  A tag “Grávida” acompanha a aluna por 40 semanas a partir dessa data.
                </p>
              </>
            ) : null}
          </div>
        ) : null}
      </ConfirmModal>
    </section>
  );
};

export default PerformanceSupportSection;
